/**
 * 사내결재 문서함 — 결재를 거치는 사내 문서의 보관·작성·통계 (v2.55.0 개명, 2026-08-05 사용자 확정).
 *   v2.53.0 '지출·경비'로 신설 → v2.55.0 판매보고서 보관함을 받아 사내결재 문서함으로
 *   → **v2.249.0 판매보고서는 영업진행 안의 [📄 판매보고서] 탭으로 옮겼다**(2026-10-14 사용자 결정).
 *
 *   분류 기준(2026-10-14 바뀜) — **「이 문서를 영업번호로 찾나?」**
 *     예 → 영업 쪽 (판매보고서 = 영업진행 탭 · 견적서·발주서·착수계 등 = 거래 문서함)
 *     아니오 (사람·돈·부서·기간의 문서) → 이 문서함
 *     결재·서명할 일은 종류와 상관없이 → [내 결재함] 한 곳
 *   8월 기준 「결재선을 도는 사내 문서」는 판보까지 끌어들였다 — 지금은 거의 모든 문서가 결재·서명을 거쳐 기준이 못 된다.
 *   ⇒ 이 문서함의 탭은 전부 사내결재 문서 표(expense_doc) 하나의 문서이고, **모든 직원(viewer 포함)이 같은 탭**을 본다.
 *
 *   ① 지출 현황        — 지출 통계 (시야 = 문서 열람 규칙과 동일, 서버가 계산)
 *   ② 지출결재 문서     — 작성 + 완결 목록
 *   ③ 법인카드 사용내역  — 카드사 엑셀 업로드·작성 시작
 *   ④ 휴일근무일지 · 부서별 월간보고서(기술지원팀·영업팀·경영지원부·연구소 — v2.280.0 에 한 탭으로) · 회의록 · 분기 재고 실사
 *      — 같은 결재선(또는 서명)을 도는 사내 문서
 *
 * 결재 진행은 그대로 [내 결재함]에서. 데이터·API·경로(/expenses)는 이동 전과 동일.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ExpenseStatsTab } from './expenses/ExpenseStatsTab';
import { ExpenseDocsTab } from './expenses/ExpenseDocsTab';
import { CardStatementTab } from './expenses/CardStatementTab';
import { HolidayWorkTab } from './expenses/HolidayWorkTab';
import { DeptMonthlyTab } from './expenses/DeptMonthlyTab';
import { MeetingMinutesTab } from './expenses/MeetingMinutesTab';
import { StockAuditReportTab } from './expenses/StockAuditReportTab';
import { useAuth } from '../lib/auth-context';
import { PageTabs } from '../components/ui/PageTabs';

type TabKey = 'stats' | 'docs' | 'card' | 'holiday' | 'monthly' | 'meeting' | 'stockAudit';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'stats', label: '지출 현황' },
  { key: 'docs', label: '지출결재 문서' },
  { key: 'card', label: '법인카드 사용내역' },
  // 지출은 아니지만 **같은 결재선**을 도는 사내 문서라 이 문서함에 둔다 (v2.97.0 사용자 요청)
  { key: 'holiday', label: '휴일근무일지' },
  // 월간보고서 세 종류를 한 탭에 (v2.280.0 사용자 「부서별 월간보고서로 통합」) — 기술지원팀(기술지원 포털이 보내 옴 · v2.113.0) ·
  //   영업팀(매출 분석에서 상신 · v2.115.0) · 경영지원부·기업부설연구소(여기서 작성 · v2.280.0). 결재선은 셋 다 3칸(경영지원 없음).
  //   ⭐부서의 **한 달** 보고라(영업건 하나의 문서가 아니다) 판매보고서와 달리 이 문서함에 남는다(v2.249.0 기준).
  { key: 'monthly', label: '부서별 월간보고서' },
  // 회의록 (v2.118.0 사용자 요청) — 여기서 직접 쓰고 보관한다. v2.229.0 부터 결재 대신 **사내 참석자 동의 서명**.
  { key: 'meeting', label: '회의록' },
  // 분기 재고 실사 보고서 (v2.224.0) — 앱의 분기 실사 화면에서 상신한 문서. 여기서는 보고 결재만. 결재선 3칸.
  { key: 'stockAudit', label: '분기 재고 실사' },
];

/**
 * 옛 탭 값 → 지금 탭 (v2.280.0) — 두 월간보고서 탭이 [부서별 월간보고서] 하나가 됐다.
 * ⚠지우지 말 것 — 「주소는 한 개도 안 없앤다」(lib/menu.ts). 즐겨찾기·이미 나간 웍스 결재 알림 링크(`?tab=tsp`·`?tab=salesReport`)가
 *   아직 이 값으로 들어온다. 아래 정규화가 주소도 새 값으로 바꿔 둔다. check:menu ④ 가 본다.
 */
const LEGACY_TAB: Record<string, TabKey> = { tsp: 'monthly', salesReport: 'monthly' };

export function ExpensesPage() {
  const { user } = useAuth();
  const isViewer = user?.role === 'viewer';
  const navigate = useNavigate();
  // 탭은 URL(?tab=)과 맞물린다 (v2.128.0) — ⚠**지우지 말 것**. 사이드바가 탭마다 문을 냈던 것은
  //   v2.135.0 에 없앴지만, 이 맞물림에 기대는 것이 셋 남아 있다: ①웍스 결재 알림 링크
  //   (`/expenses?tab=docs` 등 — backend lib/approval/expense.ts) ②화면끼리 보내는 링크
  //   (매출분석→영업팀 월간보고서 등) ③브라우저 탭 제목(창을 여럿 띄웠을 때). 주소는 /expenses 그대로.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tabParam: string | null = rawTab !== null ? (LEGACY_TAB[rawTab] ?? rawTab) : null;
  // ⭐옛 주소 넘김 (v2.249.0) — 판매보고서 보관함이 이 문서함의 첫 탭(`?tab=report`)이던 때의 즐겨찾기·링크.
  //   영업 계정은 새 자리(영업진행 › 판매보고서)로 보낸다. viewer 는 원래 못 보던 탭이라 아래 정규화가 지출 현황으로 바꾼다.
  //   ⚠지우지 말 것 — 「주소는 한 개도 안 없앤다」(lib/menu.ts) · check:menu ④ 가 본다.
  const legacyReport = tabParam === 'report' && !isViewer;
  useEffect(() => {
    if (legacyReport) navigate('/sales?tab=report', { replace: true });
  }, [legacyReport, navigate]);
  const isTabKey = (v: string | null): v is TabKey => TABS.some((t) => t.key === v);
  // 첫 탭 = 지출 현황 (모든 계정이 같다 — v2.249.0 전에는 영업 계정만 판매보고서가 첫 탭이었다)
  const [active, setActiveState] = useState<TabKey>(isTabKey(tabParam) ? tabParam : 'stats');
  // URL → 화면 (다른 화면의 링크로 탭이 바뀌었을 때)
  useEffect(() => {
    if (isTabKey(tabParam) && tabParam !== active) setActiveState(tabParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam]);
  // 주소 정규화 — 탭이 없거나(옛 링크 /expenses) 모르는 값이면 지금 보이는 탭을 replace 로 싣는다.
  //   그래야 브라우저 탭 제목이 「어느 탭에 있나」를 정확히 보이고, 뒤로가기 역사에는 안 쌓인다.
  //   옛 판매보고서 주소는 위에서 영업진행으로 넘기는 중이라 덮지 않는다.
  useEffect(() => {
    if (legacyReport) return;
    // ⚠rawTab 으로 견준다 — 옛 값(tsp·salesReport)으로 들어왔을 때 주소도 새 값(monthly)으로 바꿔 두기 위해
    if (rawTab !== active) {
      const sp = new URLSearchParams(searchParams);
      sp.set('tab', active);
      setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab, active, legacyReport]);
  // 화면 → URL (탭 버튼을 눌렀을 때) — replace 라 뒤로가기 역사에 안 쌓인다
  const setActive = (t: TabKey) => {
    setActiveState(t);
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', t);
    setSearchParams(sp, { replace: true });
  };

  // 영업진행으로 넘어가는 한 순간 — 지출 현황을 그렸다가 바로 사라지지 않게 비워 둔다
  if (legacyReport) return null;

  return (
    <div className="p-4 md:p-8">
      <div className="mb-3">
        <h1 className="ui-page-title">사내결재 문서함</h1>
        <p className="ui-page-sub">
          결재를 거치는 사내 문서(지출결재·법인카드·휴일근무일지·부서별 월간보고서·회의록·분기 재고 실사)의 완결 보관과 작성, 지출 통계.
          진행 중인 결재는 <Link to="/approvals" className="text-blue-600 underline">내 결재함</Link>에서.
        </p>
        {/* 옮긴 자리 안내 (v2.249.0) — 판매보고서를 여기서 찾던 습관이 남아 있는 동안. viewer 는 원래 못 보던 것이라 안 띄운다. */}
        {!isViewer && (
          <p className="text-xs text-slate-500 mt-1">
            📄 판매보고서 보관함은{' '}
            <Link to="/sales?tab=report" className="text-blue-600 underline">영업진행 › 판매보고서</Link>{' '}
            탭으로 옮겼습니다.
          </p>
        )}
      </div>

      <PageTabs tabs={TABS} value={active} onChange={setActive} ariaLabel="사내결재 문서함 보기" />

      {active === 'stats' ? (
        <ExpenseStatsTab />
      ) : active === 'docs' ? (
        <ExpenseDocsTab />
      ) : active === 'card' ? (
        <CardStatementTab />
      ) : active === 'monthly' ? (
        <DeptMonthlyTab />
      ) : active === 'meeting' ? (
        <MeetingMinutesTab />
      ) : active === 'stockAudit' ? (
        <StockAuditReportTab />
      ) : (
        // ⚠맨 끝은 **분기 없는 떨이**다 — 탭을 늘리면서 가지를 빼면 조용히 휴일근무일지가 뜬다
        <HolidayWorkTab />
      )}
    </div>
  );
}
